import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { MaskText } from "@/components/MaskText";
import { Seo } from "@/components/Seo";
import { getPostsByCategory } from "@/data/blog";

const CATEGORIES = ["Tutti", "Guide", "Strategie", "Content", "News"];

const formatDate = (d) =>
  new Date(d).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });

export default function Blog() {
  const [active, setActive] = useState("Tutti");
  const posts = getPostsByCategory(active);

  return (
    <div data-testid="blog-page">
      <Seo
        title="Blog Affiliate Marketing | TopAffiliation"
        description="Guide, consigli e news dal mondo dell'affiliate marketing. Strategie per aumentare i tuoi guadagni con scommesse, casinò e trading."
        path="/blog"
      />
      <section className="relative pt-40 pb-14 md:pt-48 md:pb-16 overflow-hidden">
        <div className="absolute inset-0 -z-10 grid-overlay opacity-50" />
        <div className="absolute -z-10 top-16 right-1/4 h-72 w-72 rounded-full bg-brand-red/15 blur-[110px]" />
        <div className="container-x">
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-12 bg-brand-red" />
            <span className="eyebrow">Il nostro blog</span>
          </div>
          <h1 className="font-heading font-extrabold tracking-[-0.03em] leading-[0.95] text-[clamp(2.5rem,7vw,5.5rem)] max-w-4xl">
            <MaskText
              trigger="load"
              delay={0.1}
              lines={[
                "Consigli e news",
                <span key="am" className="text-gradient">
                  sull'Affiliate Marketing
                </span>,
              ]}
            />
          </h1>
          <p className="mt-7 text-lg text-white/60 max-w-2xl leading-relaxed">
            Uno strumento gratuito per rimanere aggiornato e trovare le migliori strategie
            per aumentare i tuoi guadagni.
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`blog-filter-${c.toLowerCase()}`}
                onClick={() => setActive(c)}
                className={`rounded-full px-5 py-2 text-sm font-medium transition-colors border ${
                  active === c
                    ? "bg-brand-red text-white border-brand-red"
                    : "border-white/15 text-white/70 hover:border-brand-red/50 hover:text-white"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-28">
        <div className="container-x">
          {posts.length === 0 ? (
            <p className="text-white/50 text-center py-20" data-testid="blog-empty">
              Nessun articolo in questa categoria.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
              {posts.map((p, i) => (
                <Reveal key={p.slug} delay={(i % 3) * 0.07}>
                  <Link
                    to={`/blog/${p.slug}`}
                    data-testid={`blog-card-${p.slug}`}
                    className="group block h-full"
                  >
                    <div className="overflow-hidden rounded-2xl border border-white/10 aspect-[16/10]">
                      <img
                        src={p.image}
                        alt={p.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    <div className="mt-5">
                      <div className="flex items-center gap-4 text-xs text-white/45">
                        <span className="font-semibold uppercase tracking-widest text-brand-red">
                          {p.category}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" /> {formatDate(p.date)}
                        </span>
                      </div>
                      <h3 className="mt-3 font-heading text-xl font-bold leading-snug group-hover:text-brand-red transition-colors">
                        {p.title}
                      </h3>
                      <p className="mt-3 text-sm text-white/55 line-clamp-2">{p.excerpt}</p>
                      <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-4">
                        <span className="inline-flex items-center gap-1 text-xs text-white/40">
                          <Clock className="h-3.5 w-3.5" /> {p.read_time} min
                        </span>
                        <span className="inline-flex items-center gap-1 text-sm font-semibold text-brand-red">
                          Leggi
                          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
